import React, { type ReactNode } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import type { ProjectContext } from '@atlassian/jira-providers-project-context/src/types';
import { RedirectToArchivedPage } from '@atlassian/jira-redirect-archived-project';
import { Redirect } from '@atlassian/jira-router';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller';
import { ProjectContextSubscriber } from '../project-context';

export type Props = {
	projectKey: string | null | undefined;
	children: ReactNode;
};

const ERROR_REPORTING_PACKAGE_ID = 'projectSettingsController';
const ERROR_REPORTING_PACKAGE_NAME = 'jiraSpaAppsSoftwareProjectSettingsController';

/**
 * Filter for all project settings routes.
 */
export const ProjectSettingsController = ({ projectKey, children }: Props) =>
	ff('mcrtb-1670-wrap-js-error-boundary-around-project-settings-controller') ? (
		<JSErrorBoundary
			id={ERROR_REPORTING_PACKAGE_ID}
			teamName="morphinecake"
			packageName={ERROR_REPORTING_PACKAGE_NAME}
			sendToPrivacyUnsafeSplunk
		>
			<AuthenticationCheck>
				<RedirectNonAdminsAndArchivedProjects projectKey={projectKey}>
					{children}
				</RedirectNonAdminsAndArchivedProjects>
			</AuthenticationCheck>
		</JSErrorBoundary>
	) : (
		<AuthenticationCheck>
			<RedirectNonAdminsAndArchivedProjects projectKey={projectKey}>
				{children}
			</RedirectNonAdminsAndArchivedProjects>
		</AuthenticationCheck>
	);

const RedirectNonAdminsAndArchivedProjects = ({ projectKey, children }: Props) => (
	<TenantContextSubscriber>
		{({ tenantContext }) => {
			if (!projectKey) {
				return <Redirect to="/jira/projects" />;
			}

			return (
				<ProjectContextSubscriber>
					{/* @ts-expect-error - TS2322 - Type '(projectContext: ProjectContext) => React.ReactNode' is not assignable to type '(arg1: ProjectContext) => ReactElement<any, string | JSXElementConstructor<any>>'. */}
					{(projectContext: ProjectContext) => {
						if (projectContext.isProjectArchived) {
							return <RedirectToArchivedPage />;
						}
						if (tenantContext.isAdmin || projectContext.isProjectAdmin) {
							return children;
						}

						return <Redirect to={`/projects/${projectContext.projectKey}`} />;
					}}
				</ProjectContextSubscriber>
			);
		}}
	</TenantContextSubscriber>
);
